import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ReferenceValue } from "@/entities/all";

export default function EditValueModal({ isOpen, onClose, onValueUpdated, value }) {
  const [formData, setFormData] = useState({
    canonical_value: '',
    value_description: '',
    is_active: true,
    fuzzy_match_children: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (value) {
      setFormData({
        canonical_value: value.canonical_value || '',
        value_description: value.value_description || '',
        is_active: value.is_active || false,
        fuzzy_match_children: value.fuzzy_match_children ?? true,
      });
    }
  }, [value, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!value) return;
    setIsSubmitting(true);
    try {
      await ReferenceValue.update(value.id, formData);
      onValueUpdated();
    } catch (error) {
      console.error('Error updating reference value:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, val) => {
    setFormData(prev => ({ ...prev, [field]: val }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Reference Value</DialogTitle>
          <DialogDescription>
            Update the details for the canonical value '{value?.canonical_value}'.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div>
            <Label htmlFor="edit_canonical_value">Canonical Value</Label>
            <Input
              id="edit_canonical_value"
              value={formData.canonical_value}
              onChange={(e) => handleChange('canonical_value', e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="edit_value_description">Description</Label>
            <Textarea
              id="edit_value_description"
              value={formData.value_description}
              onChange={(e) => handleChange('value_description', e.target.value)}
              placeholder="Optional description of this value..."
              rows={3}
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="edit_is_active">Active Status</Label>
              <p className="text-[0.8rem] text-muted-foreground">
                Controls whether this value is currently active and available.
              </p>
            </div>
            <Switch
              id="edit_is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => handleChange('is_active', checked)}
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="edit_fuzzy_match_children">Fuzzy Match Children</Label>
              <p className="text-[0.8rem] text-muted-foreground">
                When this value is a parent, controls whether child values can be fuzzy matched.
              </p>
            </div>
            <Switch
              id="edit_fuzzy_match_children"
              checked={formData.fuzzy_match_children}
              onCheckedChange={(checked) => handleChange('fuzzy_match_children', checked)}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update Value'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
